// EventDetail.js – Детальная страница события
import React from "react";
import { useParams, Link } from "react-router-dom";
import { useEvent } from "../api/events";
import SEO from "../components/SEO";
import { getImageUrl } from "../utils/imageUtils";
import { buildProfilePath } from "../utils/profileUrl";
import { CalendarDays, MapPin, User, ExternalLink, StarIcon } from "lucide-react";

function formatDateTime(dt) {
    if (!dt) return "";
    const d = new Date(dt);
    return d.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    //    hour: "2-digit",
    //    minute: "2-digit",
    });
}

const EventDetail = () => {
    const { id } = useParams();
    const { data: event, isLoading, error } = useEvent(id);

    if (isLoading) {
        return <div className="py-16 text-center text-secondary-600">Загрузка события…</div>;
    }
    if (error || !event) {
        return <div className="py-16 text-center text-red-600">Событие не найдено.</div>;
    }

    const ownerProfileUrl = buildProfilePath({
        profile: {
            username_slug: event.owner?.username_slug,
            username: event.owner?.username,
            id: event.owner_user_id,
        },
        categoryPath: {
            main: { slug: event.owner?.category?.main_slug },
            sub: { slug: event.owner?.category?.sub_slug },
            subSub: { slug: event.owner?.category?.sub_sub_slug },
        },
    });

    // JSON-LD für SEO
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Event",
        name: event.title,
        description: event.description,
        startDate: event.start_at,
        endDate: event.end_at,
        eventStatus: event.status === "cancelled" ? "EventCancelled" : "EventScheduled",
        location: {
            "@type": "Place",
            name: event.venue || event.city || "",
            address: event.city || "",
        },
        image: event.image_url ? getImageUrl(event.image_url) : undefined,
        organizer: {
            "@type": "Person",
            name: event.owner?.title || event.owner?.username,
            url: ownerProfileUrl,
        },
        url: `/events/${event.id}`,
    };

    return (
        <>
            <SEO
                title={event.title}
                description={event.description}
                url={`/events/${event.id}`}
                type="event"
                canonical={`/events/${event.id}`}
                structuredData={structuredData}
            />
            <div className="max-w-3xl mx-auto px-4 py-8">
                <Link to="/events" className="text-primary-700 hover:underline text-sm mb-4 inline-block">
                    &larr; К списку событий
                </Link>
                <div className="bg-white/80 dark:bg-secondary-900/80 rounded-xl shadow-soft p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                        {event.image_url && (
                            <div className="md:w-72 md:flex-shrink-0">
                                <img
                                    src={getImageUrl(event.image_url)}
                                    alt={event.title}
                                    className="w-full h-64 md:h-72 object-cover rounded-lg"
                                    loading="lazy"
                                />
                            </div>
                        )}

                        <div className="min-w-0 flex-1 flex flex-col gap-4">
                            <h1 className="text-2xl font-bold text-secondary-900 flex items-center gap-2">
                                <StarIcon className="w-6 h-6 text-primary-600 flex-shrink-0" />
                                <span className="break-words">{event.title}</span>
                            </h1>

                            <div className="flex items-center gap-3 text-secondary-700 text-sm">
                                <CalendarDays className="w-4 h-4 flex-shrink-0" />
                                <span className="break-words">
                                    {formatDateTime(event.start_at)}
                                    {event.end_at && <>– {formatDateTime(event.end_at)}</>}
                                </span>
                            </div>
                            <div className="flex items-center gap-3 text-secondary-700 text-sm">
                                <MapPin className="w-4 h-4 flex-shrink-0" />
                                <span className="break-words">
                                    {event.venue && <>{event.venue}, </>}
                                    {event.city}
                                </span>
                            </div>
                            <div className="flex items-center gap-3 text-secondary-700 text-sm">
                                <User className="w-4 h-4 flex-shrink-0" />
                                <Link to={ownerProfileUrl} className="hover:underline">
                                    {event.owner?.title || event.owner?.username}
                                </Link>
                            </div>

                            {(event.link_url || event.tickets_url) && (
                                <div className="flex flex-wrap gap-2 pt-1">
                                    {event.link_url && (
                                        <a
                                            href={event.link_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-primary-700 hover:underline text-sm"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                            Подробнее
                                        </a>
                                    )}
                                    {event.tickets_url && (
                                        <a
                                            href={event.tickets_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-primary-700 hover:underline text-sm"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                            Купить билеты
                                        </a>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {event.description && (
                        <div className="mt-6 text-secondary-900 whitespace-pre-line">{event.description}</div>
                    )}
                </div>
            </div>
        </>
    );
};

export default EventDetail;
